/**
 * @lines98/client/lib/leaderboard-pane-signature — стабильная строка-сигнатура ВИДИМОГО
 * содержимого одной страницы лидерборда, для ИДЕМПОТЕНТНОГО reconcile тела борда.
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-bug 2026-06-11 «лидерборд мелькает при старте»):
 * `loadPane()` после fetch БЕЗУСЛОВНО перерисовывал тело (`rowsScroll.innerHTML = …`,
 * полный rebuild) даже когда server-truth == уже показанному снимку (instant-ui-hydration
 * §3 рисует строки из снимка МГНОВЕННО, затем fetch перетирает теми же строками) → игрок
 * ВИДИТ лишний rebuild = мелькание + дёрганье `nameFit`. Лекарство (как в jewelry-card /
 * viewerLevel): сравнить «форму» новой страницы с уже ОТРИСОВАННОЙ для этого ключа; равно →
 * no-op (DOM не трогаем). Отличается → перерисовываем (обновление видно — это желаемо, напр.
 * новый игрок появился). Один общий механизм сигнатур — `lib/reconcile-guard.signatureOf`
 * (НЕ копипаст; та же «ствол → ветки» модель, что `jewelryModelSignature`).
 *
 * SCOPE: чистая функция-проекция «состояние страницы → строка» по ВИДИМЫМ полям (rank /
 * name / tag / score|karat / moves / own-флаг / short_id-видимость + `viewerLevel` бейджа +
 * сам `status`). Включаем `status`, чтобы скелет/loading/error НЕ совпадали с 'ready' →
 * первый ready всегда отрисуется (guard не «съест» переход loading→ready).
 *
 * NOT IN SCOPE: рендер DOM (это screen), сеть, снимок (`leaderboard-snapshot`), решение о
 * teaser/playable (server-truth `short_id`, мы лишь отражаем его в сигнатуре).
 *
 * **Server-truth/анти-чит цел:** сигнатура — ЧИСТО UX-оптимизация показа; не «решает»
 * видимость, не входит в проверки/движок. Если server-truth реально другой — сигнатура
 * отличается → reconcile перерисует server-truth'ом (instant-hydration инвариант).
 *
 * Cross-references:
 *   - lib/reconcile-guard.ts (signatureOf — общий «ствол» сборки сигнатуры).
 *   - components/jewelry-card.ts (jewelryModelSignature — тот же приём для карточки).
 *   - screens/leaderboard-screen.ts (consumer: gate reconcile-перерисовки в loadPane).
 *   - .claude/skills/instant-ui-hydration/SKILL.md §3 (reconcile без скачка).
 */

import { signatureOf } from './reconcile-guard.js';

/** Видимые поля score-строки, влияющие на отрисовку (подмножество `RowVm`). */
export interface PaneSigScoreRow {
  readonly rank: number;
  readonly playerName: string;
  readonly playerTag: string;
  /** Уровень-бейдж у строки (display-only); смена уровня → строка перерисовывается. */
  readonly level: number | null;
  readonly scoreVerified: number;
  readonly movesCount: number;
  readonly isViewer: boolean;
  /** Видимость реплея (server §6.3): present → playable; null → teaser. */
  readonly shortId: string | null;
}

/** Видимые поля karat-строки (борд мастер-ювелира; без счёта/ходов/реплея). */
export interface PaneSigKaratRow {
  readonly rank: number;
  readonly playerName: string;
  readonly playerTag: string;
  /** Уровень-бейдж у строки (display-only); смена уровня → строка перерисовывается. */
  readonly level: number | null;
  readonly karat: number;
  readonly isViewer: boolean;
}

/**
 * Состояние страницы для сигнатуры — дискриминировано по `status` (ровно как `LoadState`
 * экрана: loading / error / ready[-karat]). loading/error не несут строк → их сигнатура
 * отличается от любой ready → первый ready всегда рисуется (guard не блокирует появление).
 */
export type PaneSigState =
  | { readonly status: 'loading' }
  | { readonly status: 'error' }
  | { readonly status: 'ready'; readonly rows: ReadonlyArray<PaneSigScoreRow> }
  | { readonly status: 'ready-karat'; readonly rows: ReadonlyArray<PaneSigKaratRow> };

/**
 * Сигнатура ВИДИМОГО содержимого страницы + текущего `viewerLevel` (бейдж у своей строки —
 * меняется → строка перерисовывается). Совпала с уже отрисованной для ключа → reconcile
 * делает no-op (нет мелькания). Отличается → перерисовка (новый игрок / изменился счёт /
 * уровень). Разделитель внутри `signatureOf` (unit separator) в данных не встречается.
 *
 * @param state — состояние страницы (status + строки).
 * @param viewerLevel — уровень зрителя для бейджа (null до загрузки / гость → бейджа нет).
 */
export function leaderboardPaneSignature(
  state: PaneSigState,
  viewerLevel: number | null,
): string {
  // Префикс статуса + уровень зрителя — общая «шапка» сигнатуры (loading/error не несут
  // строк, но их статус-шапка уже уникальна → первый ready гарантированно отличается).
  const head = signatureOf('v1', state.status, viewerLevel);
  if (state.status === 'loading' || state.status === 'error') {
    return head;
  }
  if (state.status === 'ready-karat') {
    const body = state.rows
      // `level` входит в сигнатуру строки → смена уровня у ЛЮБОГО игрока → reconcile.
      .map((r) => signatureOf(r.rank, r.playerName, r.playerTag, r.level, r.karat, r.isViewer))
      .join(''); // record separator между строками — не встречается в данных.
    return `${head}${state.rows.length}${body}`;
  }
  const body = state.rows
    .map((r) =>
      signatureOf(
        r.rank,
        r.playerName,
        r.playerTag,
        // `level` (бейдж у каждого игрока) → смена уровня у любой строки → reconcile.
        r.level,
        r.scoreVerified,
        r.movesCount,
        r.isViewer,
        // short_id-ВИДИМОСТЬ (playable vs teaser) важна, но сам id-строка тоже (открытие
        // реплея по нему) → включаем значение/`null` напрямую.
        r.shortId,
      ),
    )
    .join('');
  return `${head}${state.rows.length}${body}`;
}
